import React from 'react';

/* ─────────────────────────────────────────────────────────────
   PHASE 1  ·  South India Journey Map
   ───────────────────────────────────────────────────────────── */

/** Simplified South India coastline + interior */
export function SouthIndiaMap({ className = '', style = {} }) {
  return (
    <svg
      viewBox="0 0 500 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
    >
      <defs>
        <radialGradient id="mapGlow" cx="55%" cy="45%" r="55%">
          <stop offset="0%" stopColor="#1a0d2e" />
          <stop offset="100%" stopColor="#0A0612" />
        </radialGradient>
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="softGlow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {/* Flight path gradients */}
        <linearGradient id="chenFlightGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF6B00" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#F5C842" stopOpacity="0.3" />
        </linearGradient>
        <linearGradient id="blrFlightGrad" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#C9902A" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#F5C842" stopOpacity="0.3" />
        </linearGradient>
        <linearGradient id="trainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8B1A1A" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#E55A00" stopOpacity="0.3" />
        </linearGradient>
        <marker id="arrowOrange" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#FF6B00" />
        </marker>
        <marker id="arrowGold" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#C9902A" />
        </marker>
        <marker id="arrowRed" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#8B1A1A" />
        </marker>
      </defs>

      {/* Ocean background */}
      <rect width="500" height="600" fill="url(#mapGlow)" />

      {/* Bay of Bengal shimmer lines */}
      {[420, 440, 460, 475, 488].map((x, i) => (
        <path
          key={i}
          d={`M${x},${80 + i * 40} Q${x + 15},${120 + i * 40} ${x},${160 + i * 40}`}
          stroke="rgba(100,150,255,0.06)"
          strokeWidth="1"
          fill="none"
        />
      ))}

      {/* Arabian Sea shimmer lines */}
      {[30, 18, 8].map((x, i) => (
        <path
          key={i}
          d={`M${x},${200 + i * 50} Q${x - 10},${240 + i * 50} ${x},${280 + i * 50}`}
          stroke="rgba(100,150,255,0.05)"
          strokeWidth="1"
          fill="none"
        />
      ))}

      {/* ── South India Landmass ── */}
      <path
        d="
          M 78,55
          C 68,75, 55,100, 52,125
          C 49,155, 47,180, 46,205
          C 47,235, 53,258, 58,278
          C 67,310, 78,338, 84,368
          C 90,398, 94,422, 97,445
          C 102,468, 112,492, 124,512
          C 134,528, 144,540, 152,550
          C 160,558, 168,563, 176,562
          C 185,562, 195,557, 205,548
          C 220,538, 234,525, 248,510
          C 268,490, 290,470, 312,448
          C 336,424, 356,398, 370,370
          C 384,342, 394,312, 400,282
          C 408,250, 412,220, 412,192
          C 412,165, 408,140, 405,115
          C 402,92, 398,72, 393,58
          L 340,50
          C 300,48, 260,48, 220,50
          L 165,53
          L 120,54
          Z
        "
        fill="#1C1028"
        stroke="rgba(201,144,42,0.25)"
        strokeWidth="1.5"
      />

      {/* Interior terrain shading */}
      <path
        d="M 140,100 C 180,90, 250,95, 310,110 C 350,120, 370,140, 360,160 C 340,185, 290,190, 250,195 C 200,200, 155,190, 140,170 C 128,155, 130,115, 140,100Z"
        fill="rgba(255,107,0,0.04)"
      />
      {/* Western Ghats ridge */}
      <path
        d="M 78,110 C 75,145, 72,185, 75,220 C 78,255, 84,285, 87,315 C 90,345, 91,375, 92,400"
        stroke="rgba(201,144,42,0.12)"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      {/* Eastern coastal plains */}
      <path
        d="M 380,140 C 385,170, 388,200, 385,230 C 380,260, 370,285, 360,310 C 345,340, 325,365, 305,390"
        stroke="rgba(201,144,42,0.08)"
        strokeWidth="3"
        fill="none"
      />

      {/* Sri Lanka */}
      <path
        d="M 338,548 C 346,545, 360,550, 365,565 C 370,578, 362,592, 352,595 C 340,598, 330,586, 330,572 C 330,560, 334,550, 338,548 Z"
        fill="#1C1028"
        stroke="rgba(201,144,42,0.2)"
        strokeWidth="1"
      />

      {/* ── CHENNAI AIRPORT ── */}
      {/* Runway cross */}
      <g transform="translate(378, 195)">
        <rect x="-10" y="-2" width="20" height="4" rx="1" fill="rgba(255,107,0,0.6)" />
        <rect x="-2" y="-8" width="4" height="16" rx="1" fill="rgba(255,107,0,0.6)" />
        {/* Glow pulse ring */}
        <circle r="14" fill="none" stroke="rgba(255,107,0,0.15)" strokeWidth="6" />
        <circle r="18" fill="none" stroke="rgba(255,107,0,0.08)" strokeWidth="3" />
      </g>
      {/* Chennai city dot */}
      <circle cx="378" cy="195" r="5" fill="#FF6B00" filter="url(#glow)" />
      <circle cx="378" cy="195" r="3" fill="#FFF8EE" />

      {/* ── BENGALURU AIRPORT ── */}
      <g transform="translate(210, 228)">
        <rect x="-10" y="-2" width="20" height="4" rx="1" fill="rgba(201,144,42,0.6)" transform="rotate(30)" />
        <rect x="-2" y="-8" width="4" height="16" rx="1" fill="rgba(201,144,42,0.6)" transform="rotate(30)" />
        <circle r="14" fill="none" stroke="rgba(201,144,42,0.15)" strokeWidth="6" />
        <circle r="18" fill="none" stroke="rgba(201,144,42,0.08)" strokeWidth="3" />
      </g>
      <circle cx="210" cy="228" r="5" fill="#C9902A" filter="url(#glow)" />
      <circle cx="210" cy="228" r="3" fill="#FFF8EE" />

      {/* ── TIRUVANNAMALAI ── */}
      {/* Sacred flame marker */}
      <g transform="translate(320, 295)">
        <path d="M0,-14 C0,-14, 8,-6, 8,2 C8,8, 4,13, 0,13 C-4,13, -8,8, -8,2 C-8,-6, 0,-14, 0,-14Z"
          fill="#FF6B00" opacity="0.8" filter="url(#softGlow)" />
        <path d="M0,-6 C0,-6, 4,0, 4,5 C4,8, 2,11, 0,11 C-2,11, -4,8, -4,5 C-4,0, 0,-6, 0,-6Z"
          fill="#FFF3A0" opacity="0.9" />
        {/* Outer glow rings */}
        <circle r="18" fill="none" stroke="rgba(255,107,0,0.2)" strokeWidth="4" />
        <circle r="25" fill="none" stroke="rgba(255,107,0,0.1)" strokeWidth="2" />
        <circle r="32" fill="none" stroke="rgba(255,107,0,0.05)" strokeWidth="1" />
      </g>
      <circle cx="320" cy="295" r="5" fill="#FF6B00" filter="url(#softGlow)" />

      {/* ── FLIGHT PATH: Chennai → Tiruvannamalai ── */}
      {/* Shadow / trail */}
      <path
        id="flightPathChennai"
        d="M 378,195 Q 400,230 320,295"
        stroke="rgba(255,107,0,0.06)"
        strokeWidth="6"
        fill="none"
      />
      {/* Main arc */}
      <path
        d="M 378,195 Q 400,230 320,295"
        stroke="url(#chenFlightGrad)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        fill="none"
        markerEnd="url(#arrowOrange)"
      />
      {/* Distance label */}
      <text x="404" y="238" fill="rgba(255,107,0,0.7)" fontSize="9" fontFamily="Cinzel, serif" letterSpacing="1">~200 km</text>

      {/* ── FLIGHT PATH: Bengaluru → Tiruvannamalai ── */}
      <path
        id="flightPathBengaluru"
        d="M 210,228 Q 210,295 320,295"
        stroke="rgba(201,144,42,0.06)"
        strokeWidth="6"
        fill="none"
      />
      <path
        d="M 210,228 Q 210,295 320,295"
        stroke="url(#blrFlightGrad)"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        fill="none"
        markerEnd="url(#arrowGold)"
      />
      <text x="172" y="262" fill="rgba(201,144,42,0.7)" fontSize="9" fontFamily="Cinzel, serif" letterSpacing="1">~200 km</text>

      {/* ── TRAIN ROUTE: Chennai → Tiruvannamalai ── */}
      {/* Track bed */}
      <path
        id="trainPath"
        d="M 378,195 C 370,230, 355,260, 320,295"
        stroke="rgba(139,26,26,0.5)"
        strokeWidth="3"
        fill="none"
      />
      {/* Rail ties */}
      {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map((t, i) => {
        // Approximate points along the cubic bezier
        const x1 = 378 + t * (320 - 378);
        const y1 = 195 + t * (295 - 195) + Math.sin(t * Math.PI) * 30;
        return (
          <line
            key={i}
            x1={x1 - 4}
            y1={y1 - 1}
            x2={x1 + 4}
            y2={y1 + 1}
            stroke="rgba(139,26,26,0.4)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        );
      })}

      {/* ── CITY LABELS ── */}
      <text x="388" y="188" fill="rgba(255,107,0,0.9)" fontSize="9.5" fontFamily="Cinzel, serif" letterSpacing="1.5">CHENNAI</text>
      <text x="388" y="200" fill="rgba(255,107,0,0.5)" fontSize="7" fontFamily="Cinzel, serif">✈ INT'L AIRPORT</text>

      <text x="218" y="222" fill="rgba(201,144,42,0.9)" fontSize="9.5" fontFamily="Cinzel, serif" letterSpacing="1.5">BENGALURU</text>
      <text x="218" y="234" fill="rgba(201,144,42,0.5)" fontSize="7" fontFamily="Cinzel, serif">✈ INT'L AIRPORT</text>

      <text x="290" y="316" fill="rgba(255,107,0,0.9)" fontSize="9.5" fontFamily="Cinzel, serif" letterSpacing="1">TIRUVANNAMALAI</text>
      <text x="298" y="328" fill="rgba(255,248,238,0.45)" fontSize="7" fontFamily="Cinzel, serif">Arunachala ▲</text>

      {/* ── COMPASS ROSE ── */}
      <g transform="translate(52, 520)">
        {/* Outer ring */}
        <circle r="22" fill="rgba(10,6,18,0.8)" stroke="rgba(201,144,42,0.3)" strokeWidth="1" />
        {/* N arrow */}
        <polygon points="0,-18 3,-8 0,-4 -3,-8" fill="#FF6B00" opacity="0.9" />
        {/* S arrow */}
        <polygon points="0,18 3,8 0,4 -3,8" fill="rgba(201,144,42,0.5)" />
        {/* E/W lines */}
        <line x1="-18" y1="0" x2="18" y2="0" stroke="rgba(201,144,42,0.35)" strokeWidth="1" />
        {/* Center */}
        <circle r="3" fill="#C9902A" />
        {/* Cardinal labels */}
        <text x="0" y="-20" textAnchor="middle" fill="#FF6B00" fontSize="7" fontFamily="Cinzel, serif">N</text>
        <text x="0" y="27" textAnchor="middle" fill="rgba(201,144,42,0.6)" fontSize="7" fontFamily="Cinzel, serif">S</text>
        <text x="23" y="3" textAnchor="start" fill="rgba(201,144,42,0.6)" fontSize="7" fontFamily="Cinzel, serif">E</text>
        <text x="-23" y="3" textAnchor="end" fill="rgba(201,144,42,0.6)" fontSize="7" fontFamily="Cinzel, serif">W</text>
      </g>

      {/* Scale bar */}
      <g transform="translate(360, 530)">
        <line x1="0" y1="0" x2="80" y2="0" stroke="rgba(201,144,42,0.4)" strokeWidth="1" />
        <line x1="0" y1="-4" x2="0" y2="4" stroke="rgba(201,144,42,0.4)" strokeWidth="1" />
        <line x1="80" y1="-4" x2="80" y2="4" stroke="rgba(201,144,42,0.4)" strokeWidth="1" />
        <text x="40" y="-6" textAnchor="middle" fill="rgba(201,144,42,0.5)" fontSize="7" fontFamily="Cinzel, serif">200 km</text>
      </g>
    </svg>
  );
}

/** Airplane icon, pointable in any direction via transform */
export function AirplaneSVG({ size = 40, color = '#FF6B00', className = '', style = {} }) {
  return (
    <svg
      viewBox="0 0 60 60"
      width={size}
      height={size}
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id="planeGlow">
          <feGaussianBlur stdDeviation="1.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* Fuselage */}
      <path
        d="M30,6 C34,6, 52,22, 54,30 C52,38, 34,54, 30,54 C28,54, 26,52, 26,50 L26,36 L10,42 L10,36 L26,30 L10,24 L10,18 L26,24 L26,10 C26,8, 28,6, 30,6Z"
        fill={color}
        filter="url(#planeGlow)"
      />
      {/* Wing highlight */}
      <path
        d="M26,27 L10,21 L10,24 L26,30Z"
        fill="rgba(255,255,255,0.25)"
      />
      {/* Engine nacelle */}
      <ellipse cx="32" cy="20" rx="4" ry="8" fill={color} opacity="0.7" />
      <ellipse cx="32" cy="40" rx="4" ry="8" fill={color} opacity="0.7" />
      {/* Cockpit window */}
      <ellipse cx="42" cy="30" rx="5" ry="3" fill="rgba(255,248,238,0.3)" />
      {/* Exhaust trail dots */}
      <circle cx="15" cy="30" r="2" fill={color} opacity="0.4" />
      <circle cx="8" cy="30" r="1.5" fill={color} opacity="0.25" />
      <circle cx="3" cy="30" r="1" fill={color} opacity="0.12" />
    </svg>
  );
}

/** Train icon */
export function TrainSVG({ size = 52, color = '#8B1A1A', className = '', style = {} }) {
  return (
    <svg
      viewBox="0 0 80 48"
      width={size}
      height={(size * 48) / 80}
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="trainBody" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor="#C0392B" />
        </linearGradient>
        <filter id="trainGlow">
          <feGaussianBlur stdDeviation="1.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* Track rails */}
      <line x1="0" y1="40" x2="80" y2="40" stroke="rgba(201,144,42,0.4)" strokeWidth="1.5" />
      <line x1="0" y1="44" x2="80" y2="44" stroke="rgba(201,144,42,0.4)" strokeWidth="1.5" />
      {[0, 10, 20, 30, 40, 50, 60, 70].map((x, i) => (
        <line key={i} x1={x} y1="39" x2={x} y2="45" stroke="rgba(201,144,42,0.3)" strokeWidth="1" />
      ))}
      {/* Engine cab */}
      <rect x="48" y="8" width="28" height="28" rx="3" fill="url(#trainBody)" filter="url(#trainGlow)" />
      {/* Engine nose (angled) */}
      <path d="M72,8 L76,12 L76,32 L72,36 L72,8Z" fill={color} opacity="0.7" />
      {/* Cab roof */}
      <rect x="50" y="5" width="22" height="5" rx="2" fill={color} opacity="0.8" />
      {/* Windows */}
      <rect x="64" y="14" width="8" height="7" rx="1" fill="rgba(255,248,238,0.35)" />
      {/* Headlight */}
      <circle cx="75" cy="22" r="3" fill="#FFF3A0" opacity="0.9" filter="url(#trainGlow)" />
      {/* Steam pipe */}
      <rect x="68" y="2" width="3" height="6" rx="1" fill={color} opacity="0.6" />
      {/* Carriage 1 */}
      <rect x="24" y="11" width="22" height="25" rx="2" fill={color} opacity="0.75" />
      <rect x="27" y="14" width="7" height="6" rx="1" fill="rgba(255,248,238,0.25)" />
      <rect x="36" y="14" width="7" height="6" rx="1" fill="rgba(255,248,238,0.25)" />
      {/* Carriage 2 */}
      <rect x="0" y="12" width="22" height="24" rx="2" fill={color} opacity="0.6" />
      <rect x="3" y="15" width="6" height="6" rx="1" fill="rgba(255,248,238,0.2)" />
      <rect x="11" y="15" width="6" height="6" rx="1" fill="rgba(255,248,238,0.2)" />
      {/* Wheels */}
      {[10, 34, 58, 70].map((x, i) => (
        <g key={i}>
          <circle cx={x} cy="40" r="5" fill={color} opacity={i < 2 ? 0.7 : 0.9} />
          <circle cx={x} cy="40" r="2.5" fill="rgba(255,248,238,0.2)" />
        </g>
      ))}
      {/* Smoke puffs */}
      <circle cx="68" cy="0" r="3" fill="rgba(255,248,238,0.15)" />
      <circle cx="72" cy="-4" r="2" fill="rgba(255,248,238,0.1)" />
    </svg>
  );
}

/** Car icon */
export function CarSVG({ size = 36, color = '#C9902A', className = '', style = {} }) {
  return (
    <svg
      viewBox="0 0 70 32"
      width={size}
      height={(size * 32) / 70}
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="carBody" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.7" />
        </linearGradient>
        <filter id="carGlow">
          <feGaussianBlur stdDeviation="1" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* Body */}
      <path
        d="M8,20 L8,14 C8,12, 10,10, 12,10 L18,8 C22,6, 28,5, 35,5 C42,5, 48,6, 52,8 L58,10 C60,10, 62,12, 62,14 L62,20Z"
        fill="url(#carBody)"
        filter="url(#carGlow)"
      />
      {/* Cabin glass */}
      <path
        d="M20,9 L18,8 C22,6, 28,5, 35,5 C42,5, 48,6, 52,8 L50,9 C46,7, 38,6, 35,6 C28,6, 22,7, 20,9Z"
        fill="rgba(255,248,238,0.3)"
      />
      {/* Windscreen */}
      <path d="M20,9 L22,14 L48,14 L50,9 C46,7, 38,6, 35,6 C28,6, 22,7, 20,9Z"
        fill="rgba(255,248,238,0.18)" />
      {/* Side windows */}
      <rect x="24" y="9.5" width="10" height="5" rx="1" fill="rgba(255,248,238,0.2)" />
      <rect x="36" y="9.5" width="10" height="5" rx="1" fill="rgba(255,248,238,0.2)" />
      {/* Underbody */}
      <rect x="6" y="19" width="58" height="4" rx="2" fill={color} opacity="0.7" />
      {/* Wheels */}
      <circle cx="18" cy="23" r="7" fill="#1C1028" stroke={color} strokeWidth="2" />
      <circle cx="18" cy="23" r="3.5" fill={color} opacity="0.5" />
      <circle cx="52" cy="23" r="7" fill="#1C1028" stroke={color} strokeWidth="2" />
      <circle cx="52" cy="23" r="3.5" fill={color} opacity="0.5" />
      {/* Headlights */}
      <ellipse cx="64" cy="16" rx="3" ry="2" fill="#FFF3A0" opacity="0.7" />
      {/* Tail light */}
      <ellipse cx="6" cy="16" rx="2.5" ry="1.8" fill="#FF4444" opacity="0.6" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
   PHASE 2  ·  Arunachala Girivalam Map
   Points sourced from Giri Pradakshina route (14 km circuit)
   ───────────────────────────────────────────────────────────── */

const LANDMARKS = [
  // 8 Ashta Lingams — clockwise from North
  {
    id: 'kubera',
    direction: 'N',
    angle: 0,
    title: 'Kubera Lingam',
    subtitle: 'North · Riches & Prosperity',
    color: '#F5C842',
    pathR: 175,
  },
  {
    id: 'isanya',
    direction: 'NE',
    angle: 45,
    title: 'Isanya Lingam',
    subtitle: 'North-East · Knowledge',
    color: '#C9902A',
    pathR: 175,
  },
  {
    id: 'indra',
    direction: 'E',
    angle: 90,
    title: 'Indra Lingam',
    subtitle: 'East · Rain & Thunder',
    color: '#FF6B00',
    pathR: 175,
  },
  {
    id: 'agni',
    direction: 'SE',
    angle: 135,
    title: 'Agni Lingam',
    subtitle: 'South-East · Fire',
    color: '#E55A00',
    pathR: 175,
  },
  {
    id: 'yama',
    direction: 'S',
    angle: 180,
    title: 'Yama Lingam',
    subtitle: 'South · Dharma',
    color: '#8B1A1A',
    pathR: 175,
  },
  {
    id: 'niruthi',
    direction: 'SW',
    angle: 225,
    title: 'Niruthi Lingam',
    subtitle: 'South-West · Liberation',
    color: '#5C0F0F',
    pathR: 175,
  },
  {
    id: 'varuna',
    direction: 'W',
    angle: 270,
    title: 'Varuna Lingam',
    subtitle: 'West · Water & Rain',
    color: '#2D1B5E',
    pathR: 175,
  },
  {
    id: 'vayu',
    direction: 'NW',
    angle: 315,
    title: 'Vayu Lingam',
    subtitle: 'North-West · Wind',
    color: '#6B4226',
    pathR: 175,
  },
  // Key Ashrams & Shrines — slightly inside/outside path
  {
    id: 'arunachaleswarar',
    angle: 105,
    title: 'Arunachaleswarar Temple',
    subtitle: 'Main Shiva Temple · Starting Point',
    color: '#FF6B00',
    pathR: 210,
    isLandmark: true,
  },
  {
    id: 'ramana',
    angle: 168,
    title: 'Ramana Maharshi Ashram',
    subtitle: 'Sri Ramanasramam · South Face',
    color: '#C9902A',
    pathR: 200,
    isLandmark: true,
  },
  {
    id: 'virupaksha',
    angle: 258,
    title: 'Virupaksha Cave',
    subtitle: 'Ramana\'s Meditation Cave · West Face',
    color: '#E8A0BF',
    pathR: 135,
    isLandmark: true,
  },
  {
    id: 'skandashram',
    angle: 285,
    title: 'Skandashram',
    subtitle: 'Hillside Ashram · North-West Face',
    color: '#E8A0BF',
    pathR: 120,
    isLandmark: true,
  },
  {
    id: 'vedanagari',
    angle: 345,
    title: 'Vedanagari Ashram',
    subtitle: '18 km North · Vellore Highway',
    color: '#F5C842',
    pathR: 245,
    isLandmark: true,
    isDestination: true,
  },
];

function lmPos(angle, r, cx = 300, cy = 300) {
  const rad = ((angle - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export function ArunachalaMap({ activeId, onHover, onClick, className = '', style = {} }) {
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
    >
      <defs>
        <radialGradient id="mountainGrad" cx="50%" cy="60%" r="50%">
          <stop offset="0%" stopColor="#3D2010" />
          <stop offset="60%" stopColor="#1C1028" />
          <stop offset="100%" stopColor="#0A0612" />
        </radialGradient>
        <radialGradient id="peakGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255,107,0,0.35)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <radialGradient id="bgGrad" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#160d24" />
          <stop offset="100%" stopColor="#0A0612" />
        </radialGradient>
        <filter id="hotspotGlow">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="mountainBlur">
          <feGaussianBlur stdDeviation="1.5" />
        </filter>
        <filter id="destinationGlow">
          <feGaussianBlur stdDeviation="5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Background */}
      <rect width="600" height="600" fill="url(#bgGrad)" />

      {/* Outer mandala rings */}
      {[280, 260, 240].map((r, i) => (
        <circle key={i} cx="300" cy="300" r={r}
          fill="none"
          stroke="rgba(201,144,42,0.06)"
          strokeWidth={i === 0 ? 1.5 : 0.8}
          strokeDasharray={i === 1 ? '3 6' : 'none'}
        />
      ))}

      {/* Girivalam path ring */}
      <circle cx="300" cy="300" r="175"
        fill="none"
        stroke="rgba(255,107,0,0.25)"
        strokeWidth="2.5"
      />
      {/* Path dotted overlay */}
      <circle cx="300" cy="300" r="175"
        fill="none"
        stroke="rgba(245,200,66,0.1)"
        strokeWidth="8"
        strokeDasharray="2 8"
      />

      {/* Kilometre markers on path */}
      {Array.from({ length: 14 }, (_, i) => {
        const angle = (i * 360) / 14;
        const { x, y } = lmPos(angle, 175);
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="2" fill="rgba(201,144,42,0.4)" />
          </g>
        );
      })}

      {/* ── Mountain silhouette (top-down view) ── */}
      {/* Shadow/base */}
      <ellipse cx="300" cy="305" rx="82" ry="68" fill="rgba(0,0,0,0.5)" filter="url(#mountainBlur)" />
      {/* Outer mountain base */}
      <path
        d="
          M 300,218
          C 320,220, 352,230, 368,248
          C 382,264, 385,282, 383,298
          C 381,318, 370,338, 352,352
          C 334,366, 318,372, 300,373
          C 282,373, 265,368, 248,354
          C 230,340, 218,320, 216,300
          C 214,280, 220,260, 234,246
          C 249,231, 278,218, 300,218 Z
        "
        fill="url(#mountainGrad)"
        stroke="rgba(201,144,42,0.2)"
        strokeWidth="1"
      />
      {/* Mid terrain */}
      <path
        d="
          M 300,238
          C 316,239, 340,248, 354,263
          C 366,276, 368,292, 365,306
          C 362,322, 350,338, 335,348
          C 320,357, 308,360, 300,360
          C 286,360, 270,355, 258,344
          C 244,332, 235,315, 234,300
          C 233,284, 240,268, 252,257
          C 265,246, 285,238, 300,238 Z
        "
        fill="#2C1810"
        stroke="rgba(201,144,42,0.15)"
        strokeWidth="0.8"
      />
      {/* Summit area */}
      <path
        d="
          M 300,262
          C 310,263, 328,270, 338,282
          C 346,292, 347,304, 344,315
          C 340,327, 330,337, 318,343
          C 309,347, 300,348, 292,346
          C 280,342, 268,334, 263,320
          C 258,307, 260,293, 268,282
          C 277,271, 290,262, 300,262 Z
        "
        fill="#3D2010"
      />
      {/* Peak glow */}
      <ellipse cx="300" cy="295" rx="45" ry="40" fill="url(#peakGlow)" />
      {/* Summit peak mark */}
      <circle cx="300" cy="295" r="10" fill="rgba(255,107,0,0.5)" />
      <circle cx="300" cy="295" r="5" fill="#FF6B00" />
      <circle cx="300" cy="295" r="2" fill="#FFF3A0" />

      {/* Mountain name */}
      <text x="300" y="388" textAnchor="middle" fill="rgba(245,200,66,0.7)"
        fontSize="10" fontFamily="Cinzel, serif" letterSpacing="3">ARUNACHALA</text>
      <text x="300" y="400" textAnchor="middle" fill="rgba(255,107,0,0.4)"
        fontSize="7.5" fontFamily="Cinzel, serif" letterSpacing="2">2,682 ft · Sacred Hill</text>

      {/* ── Girivalam route label ── */}
      <path id="routeTextPath" d="M 125,300 A 175,175 0 0,1 475,300" fill="none" />
      <text fill="rgba(255,107,0,0.4)" fontSize="8" fontFamily="Cinzel, serif" letterSpacing="4">
        <textPath href="#routeTextPath" startOffset="15%">GIRIVALAM ROUTE · 14 KM PRADAKSHINA PATH</textPath>
      </text>

      {/* ── Ashta Lingam Hotspots ── */}
      {LANDMARKS.filter(l => !l.isLandmark).map(lm => {
        const { x, y } = lmPos(lm.angle, lm.pathR);
        const isActive = activeId === lm.id;
        return (
          <g
            key={lm.id}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => onHover && onHover(lm.id)}
            onMouseLeave={() => onHover && onHover(null)}
            onClick={() => onClick && onClick(lm)}
          >
            {/* Pulse ring */}
            <circle cx={x} cy={y} r={isActive ? 18 : 14}
              fill="none"
              stroke={lm.color}
              strokeWidth={isActive ? 1.5 : 0.8}
              opacity={isActive ? 0.8 : 0.35}
            />
            {/* Outer glow */}
            <circle cx={x} cy={y} r="10"
              fill={lm.color}
              opacity={isActive ? 0.25 : 0.1}
              filter="url(#hotspotGlow)"
            />
            {/* Main dot */}
            <circle cx={x} cy={y} r={isActive ? 7 : 5}
              fill={lm.color}
              opacity={isActive ? 1 : 0.75}
            />
            <circle cx={x} cy={y} r="2.5" fill="rgba(255,248,238,0.8)" />
            {/* Direction label */}
            <text
              x={x + (lm.angle > 90 && lm.angle < 270 ? -16 : 10)}
              y={y + (lm.angle > 180 ? 14 : -8)}
              fill={lm.color}
              fontSize="7.5"
              fontFamily="Cinzel, serif"
              letterSpacing="1"
              opacity={isActive ? 1 : 0.65}
            >
              {lm.direction}
            </text>
          </g>
        );
      })}

      {/* ── Key Landmarks ── */}
      {LANDMARKS.filter(l => l.isLandmark).map(lm => {
        const { x, y } = lmPos(lm.angle, lm.pathR);
        const isActive = activeId === lm.id;
        const isDest = lm.isDestination;
        return (
          <g
            key={lm.id}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => onHover && onHover(lm.id)}
            onMouseLeave={() => onHover && onHover(null)}
            onClick={() => onClick && onClick(lm)}
          >
            {isDest ? (
              <>
                {/* Vedanagari special marker */}
                <circle cx={x} cy={y} r="20"
                  fill="none"
                  stroke={lm.color}
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                  opacity={isActive ? 0.9 : 0.5}
                  filter="url(#destinationGlow)"
                />
                <circle cx={x} cy={y} r="10" fill={lm.color} opacity={0.2} filter="url(#destinationGlow)" />
                <path d={`M${x},${y - 12} L${x + 7},${y + 4} L${x - 7},${y + 4}Z`}
                  fill={lm.color} opacity={isActive ? 1 : 0.8}
                  filter="url(#destinationGlow)"
                />
                <circle cx={x} cy={y - 12} r="4" fill={lm.color} opacity={0.9} />
                {/* Label line + text */}
                <line x1={x} y1={y + 4} x2={x} y2={y + 20}
                  stroke={lm.color} strokeWidth="1" opacity="0.4" />
                <text x={x} y={y + 30} textAnchor="middle"
                  fill={lm.color} fontSize="8" fontFamily="Cinzel, serif" letterSpacing="1">
                  Vedanagari ▲
                </text>
                <text x={x} y={y + 40} textAnchor="middle"
                  fill="rgba(245,200,66,0.4)" fontSize="6.5" fontFamily="Cinzel, serif">
                  18 km North
                </text>
              </>
            ) : (
              <>
                {/* Diamond shape for landmark */}
                <polygon
                  points={`${x},${y - 7} ${x + 6},${y} ${x},${y + 7} ${x - 6},${y}`}
                  fill={lm.color}
                  opacity={isActive ? 0.9 : 0.55}
                />
                <circle cx={x} cy={y} r="2.5" fill="rgba(255,248,238,0.7)" />
                {/* Pulse on active */}
                {isActive && (
                  <circle cx={x} cy={y} r="12" fill="none"
                    stroke={lm.color} strokeWidth="1" opacity="0.5" />
                )}
              </>
            )}
          </g>
        );
      })}

      {/* ── Compass Rose ── */}
      <g transform="translate(545, 52)">
        <circle r="28" fill="rgba(10,6,18,0.85)" stroke="rgba(201,144,42,0.25)" strokeWidth="1" />
        {/* N pointer */}
        <polygon points="0,-22 3,-10 0,-6 -3,-10" fill="#FF6B00" />
        {/* S pointer */}
        <polygon points="0,22 3,10 0,6 -3,10" fill="rgba(201,144,42,0.45)" />
        {/* E/W */}
        <polygon points="22,0 10,3 6,0 10,-3" fill="rgba(201,144,42,0.45)" />
        <polygon points="-22,0 -10,3 -6,0 -10,-3" fill="rgba(201,144,42,0.45)" />
        {/* Intercardinals */}
        {[45, 135, 225, 315].map(a => {
          const r = (a * Math.PI) / 180;
          return (
            <line key={a}
              x1={12 * Math.cos(r - Math.PI / 2)} y1={12 * Math.sin(r - Math.PI / 2)}
              x2={20 * Math.cos(r - Math.PI / 2)} y2={20 * Math.sin(r - Math.PI / 2)}
              stroke="rgba(201,144,42,0.3)" strokeWidth="1"
            />
          );
        })}
        <circle r="4" fill="#C9902A" />
        <text x="0" y="-24" textAnchor="middle" fill="#FF6B00" fontSize="9" fontFamily="Cinzel, serif">N</text>
        <text x="0" y="32" textAnchor="middle" fill="rgba(201,144,42,0.6)" fontSize="7" fontFamily="Cinzel, serif">S</text>
        <text x="26" y="3" textAnchor="start" fill="rgba(201,144,42,0.6)" fontSize="7" fontFamily="Cinzel, serif">E</text>
        <text x="-26" y="3" textAnchor="end" fill="rgba(201,144,42,0.6)" fontSize="7" fontFamily="Cinzel, serif">W</text>
      </g>

      {/* ── Legend ── */}
      <g transform="translate(16, 430)">
        <rect width="130" height="120" rx="2" fill="rgba(10,6,18,0.75)" stroke="rgba(201,144,42,0.15)" strokeWidth="1" />
        <text x="10" y="16" fill="rgba(245,200,66,0.7)" fontSize="7.5" fontFamily="Cinzel, serif" letterSpacing="2">LEGEND</text>
        <line x1="8" y1="20" x2="122" y2="20" stroke="rgba(201,144,42,0.2)" strokeWidth="0.5" />
        {/* Ashta Lingam */}
        <circle cx="18" cy="34" r="5" fill="#FF6B00" opacity="0.8" />
        <circle cx="18" cy="34" r="2" fill="rgba(255,248,238,0.8)" />
        <text x="30" y="38" fill="rgba(255,248,238,0.6)" fontSize="7.5" fontFamily="Cinzel, serif">Ashta Lingam</text>
        {/* Landmark */}
        <polygon points="18,48 24,55 18,62 12,55" fill="#C9902A" opacity="0.7" />
        <text x="30" y="58" fill="rgba(255,248,238,0.6)" fontSize="7.5" fontFamily="Cinzel, serif">Landmark / Ashram</text>
        {/* Girivalam path */}
        <line x1="10" y1="75" x2="26" y2="75" stroke="rgba(255,107,0,0.6)" strokeWidth="2" strokeDasharray="3 2" />
        <text x="30" y="78" fill="rgba(255,248,238,0.6)" fontSize="7.5" fontFamily="Cinzel, serif">Girivalam Path</text>
        {/* Destination */}
        <polygon points="18,89 24,101 12,101" fill="#F5C842" opacity="0.8" />
        <text x="30" y="98" fill="rgba(255,248,238,0.6)" fontSize="7.5" fontFamily="Cinzel, serif">Vedanagari Ashram</text>
      </g>
    </svg>
  );
}

/** Tooltip card data accessor — returns landmark by id */
export { LANDMARKS };

/* ─────────────────────────────────────────────────────────────
   PHASE 3  ·  Road Approach to Vedanagari Ashram
   18 km north from Arunachala on Vellore Highway
   ───────────────────────────────────────────────────────────── */

export function AshramApproach({ progress = 1, className = '', style = {} }) {
  // progress: 0..1 controls how far the road reveal has drawn
  const roadReveal = Math.min(1, Math.max(0, progress));
  const dashLen = 420 * roadReveal;

  return (
    <svg
      viewBox="0 0 400 560"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
    >
      <defs>
        <linearGradient id="roadGrad" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#FF6B00" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#F5C842" stopOpacity="0.9" />
        </linearGradient>
        <radialGradient id="ashramGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(245,200,66,0.4)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <radialGradient id="mountainTopGlow" cx="50%" cy="40%" r="50%">
          <stop offset="0%" stopColor="rgba(255,107,0,0.3)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <filter id="approachGlow">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="ashramFilter">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <clipPath id="roadClip">
          <rect x="0" y="0" width="400" height="560" />
        </clipPath>
      </defs>

      {/* Night sky / background */}
      <rect width="400" height="560" fill="#0A0612" />
      {/* Stars */}
      {[
        [40,30],[80,15],[130,45],[200,20],[270,35],[330,18],[380,50],
        [60,80],[150,90],[250,70],[350,85],[20,120],[380,130],
        [100,140],[300,150],[180,60],[420,40],[10,60],
      ].map(([sx, sy], i) => (
        <circle key={i} cx={sx} cy={sy} r={i % 3 === 0 ? 1.5 : 1} fill="rgba(255,248,238,0.5)" />
      ))}

      {/* ── Arunachala silhouette at bottom ── */}
      <ellipse cx="200" cy="490" rx="200" ry="80" fill="rgba(28,16,40,0.9)" />
      <path
        d="
          M 0,520
          L 40,490 L 70,480 L 100,470 L 130,462
          L 155,455 L 175,448 L 195,442
          C 197,440, 200,435, 200,432
          C 200,435, 203,440, 205,442
          L 225,448 L 248,455 L 275,462
          L 310,472 L 345,482 L 380,492
          L 400,500 L 400,560 L 0,560 Z
        "
        fill="#1C1028"
      />
      {/* Mountain peak glow */}
      <ellipse cx="200" cy="432" rx="60" ry="35" fill="url(#mountainTopGlow)" />
      {/* Peak dot */}
      <circle cx="200" cy="432" r="6" fill="rgba(255,107,0,0.6)" filter="url(#approachGlow)" />
      <circle cx="200" cy="432" r="3" fill="#FF6B00" />
      <text x="200" y="420" textAnchor="middle" fill="rgba(255,107,0,0.5)"
        fontSize="8" fontFamily="Cinzel, serif" letterSpacing="2">ARUNACHALA</text>

      {/* ── Road ── */}
      {/* Road bed / tarmac */}
      <path
        d="M 188,428 L 175,300 L 168,180 L 165,100 L 165,55"
        stroke="rgba(40,30,60,0.9)"
        strokeWidth="32"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M 212,428 L 225,300 L 232,180 L 235,100 L 235,55"
        stroke="rgba(40,30,60,0.9)"
        strokeWidth="32"
        strokeLinecap="round"
        fill="none"
      />
      {/* Road surface */}
      <path
        d="M 200,430 L 200,300 L 200,180 L 200,55"
        stroke="rgba(30,20,50,0.95)"
        strokeWidth="58"
        fill="none"
      />
      {/* Road edges */}
      <path
        d="M 172,428 L 165,300 L 162,180 L 160,55"
        stroke="rgba(201,144,42,0.2)"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M 228,428 L 235,300 L 238,180 L 240,55"
        stroke="rgba(201,144,42,0.2)"
        strokeWidth="1.5"
        fill="none"
      />
      {/* Center dashes — animated reveal */}
      <path
        d="M 200,430 L 200,10"
        stroke="rgba(245,200,66,0.55)"
        strokeWidth="2"
        strokeDasharray="16 12"
        strokeDashoffset={420 - dashLen}
        fill="none"
        strokeLinecap="round"
      />

      {/* ── Distance markers ── */}
      {[
        { km: 5, y: 360, label: '5 km' },
        { km: 10, y: 270, label: '10 km' },
        { km: 15, y: 160, label: '15 km' },
        { km: 18, y: 88, label: '18 km' },
      ].map(({ km, y, label }) => (
        <g key={km} opacity={roadReveal > (km / 20) ? 1 : 0.15}
          style={{ transition: 'opacity 0.5s ease' }}>
          {/* Marker line left */}
          <line x1="145" y1={y} x2="168" y2={y} stroke="rgba(201,144,42,0.4)" strokeWidth="1" />
          {/* Marker line right */}
          <line x1="232" y1={y} x2="255" y2={y} stroke="rgba(201,144,42,0.4)" strokeWidth="1" />
          {/* Labels */}
          <text x="134" y={y + 4} textAnchor="end"
            fill="rgba(201,144,42,0.6)" fontSize="8" fontFamily="Cinzel, serif">{label}</text>
        </g>
      ))}

      {/* ── Trees along road ── */}
      {[
        // [x, y, size, side] — left side
        [148, 390, 1.0, -1], [143, 340, 0.85, -1], [140, 280, 0.9, -1],
        [138, 220, 0.8, -1], [136, 160, 0.75, -1], [135, 110, 0.7, -1],
        // right side
        [252, 390, 1.0, 1], [257, 345, 0.85, 1], [260, 285, 0.9, 1],
        [262, 225, 0.8, 1], [264, 162, 0.75, 1], [265, 112, 0.7, 1],
      ].map(([tx, ty, ts, side], i) => (
        <g key={i} transform={`translate(${tx}, ${ty}) scale(${ts})`}
          opacity={roadReveal > 0.2 ? 0.7 : 0.1}
          style={{ transition: 'opacity 0.6s ease' }}>
          {/* Tree trunk */}
          <rect x="-2" y="8" width="4" height="10" fill="#2C1810" />
          {/* Foliage layers */}
          <ellipse cx="0" cy="6" rx="9" ry="7" fill="#1a2e10" opacity="0.9" />
          <ellipse cx="0" cy="0" rx="7" ry="6" fill="#223a14" opacity="0.8" />
          <ellipse cx="0" cy="-5" rx="5" ry="5" fill="#2a4818" opacity="0.7" />
          {/* Palm fronds for south Indian look */}
          <path d={`M0,${side > 0 ? -8 : -8} C${side * 10},-14, ${side * 16},-10, ${side * 18},-5`}
            stroke="#2a4818" strokeWidth="1.5" fill="none" />
        </g>
      ))}

      {/* ── Highway label ── */}
      <text x="280" y="320" fill="rgba(201,144,42,0.35)"
        fontSize="7.5" fontFamily="Cinzel, serif" letterSpacing="1.5" transform="rotate(-90, 280, 320)">
        VELLORE HIGHWAY (NH 79)
      </text>

      {/* ── North Arrow ── */}
      <g transform="translate(358, 200)">
        <circle r="20" fill="rgba(10,6,18,0.8)" stroke="rgba(201,144,42,0.25)" strokeWidth="1" />
        <polygon points="0,-14 3,-6 0,-2 -3,-6" fill="#FF6B00" />
        <polygon points="0,14 3,6 0,2 -3,6" fill="rgba(201,144,42,0.4)" />
        <text x="0" y="-16" textAnchor="middle" fill="#FF6B00" fontSize="8" fontFamily="Cinzel, serif">N</text>
        <circle r="3" fill="#C9902A" />
      </g>

      {/* ── Vedanagari Ashram ── */}
      <g transform="translate(200, 48)" opacity={roadReveal > 0.85 ? 1 : 0.2}
        style={{ transition: 'opacity 0.8s ease' }}>
        {/* Glow halo */}
        <ellipse cx="0" cy="4" rx="55" ry="30" fill="url(#ashramGlow)" />
        {/* Main gopuram silhouette */}
        <g filter="url(#ashramFilter)">
          {/* Central tower */}
          <polygon points="0,-28 8,-8 -8,-8" fill="#F5C842" opacity="0.9" />
          <rect x="-10" y="-8" width="20" height="18" rx="1" fill="#C9902A" opacity="0.85" />
          {/* Side towers */}
          <polygon points="-18,-18 -12,-4 -24,-4" fill="#C9902A" opacity="0.7" />
          <polygon points="18,-18 12,-4 24,-4" fill="#C9902A" opacity="0.7" />
          <rect x="-26" y="-4" width="14" height="14" rx="1" fill="#C9902A" opacity="0.65" />
          <rect x="12" y="-4" width="14" height="14" rx="1" fill="#C9902A" opacity="0.65" />
          {/* Base wall */}
          <rect x="-38" y="10" width="76" height="8" rx="1" fill="#8B4513" opacity="0.7" />
          {/* Flag */}
          <line x1="0" y1="-28" x2="0" y2="-40" stroke="#F5C842" strokeWidth="1" />
          <polygon points="0,-40 8,-35 0,-30" fill="#FF6B00" opacity="0.8" />
        </g>
        {/* Labels */}
        <text x="0" y="32" textAnchor="middle" fill="#F5C842"
          fontSize="9.5" fontFamily="Cinzel, serif" letterSpacing="2">VEDANAGARI ASHRAM</text>
        <text x="0" y="44" textAnchor="middle" fill="rgba(255,107,0,0.6)"
          fontSize="7.5" fontFamily="Cinzel, serif" letterSpacing="1">Tiruvannamalai · 10 Acres</text>
      </g>

      {/* ── Direction heading ── */}
      <text x="200" y="500" textAnchor="middle" fill="rgba(201,144,42,0.3)"
        fontSize="7" fontFamily="Cinzel, serif" letterSpacing="3">FROM ARUNACHALA · VELLORE HIGHWAY</text>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
   SHARED  ·  Arunachala Mountain Silhouette (hero/bg use)
   ───────────────────────────────────────────────────────────── */

export function ArunachalaSilhouette({ width = 600, height = 200, color = '#1C1028', className = '', style = {} }) {
  return (
    <svg
      viewBox="0 0 600 200"
      width={width}
      height={height}
      preserveAspectRatio="xMidYMax meet"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
    >
      <defs>
        <linearGradient id="hillGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.5" />
        </linearGradient>
      </defs>
      {/* Distant ridgeline */}
      <path
        d="M0,180 C50,175, 100,170, 150,172 C180,173, 200,165, 220,168 C240,170, 260,175, 300,178 C340,180, 380,176, 420,172 C460,168, 500,170, 550,174 L600,178 L600,200 L0,200Z"
        fill={color}
        opacity="0.4"
      />
      {/* Main mountain mass */}
      <path
        d="
          M 0,200
          L 20,195 L 60,190 L 100,185 L 140,180
          L 175,175 L 205,165 L 225,152 L 238,140
          L 248,128 L 255,115 L 262,100 L 268,85
          L 274,72 L 279,60 L 284,50 L 288,40
          L 291,33 L 294,27 L 297,22 L 299,18
          L 300,15
          L 301,18 L 303,22 L 306,27 L 309,33
          L 313,40 L 318,50 L 323,60 L 328,72
          L 334,85 L 340,100 L 347,115 L 354,128
          L 364,140 L 376,152 L 396,165 L 420,174
          L 460,181 L 500,186 L 540,190 L 580,194 L 600,198
          L 600,200 Z
        "
        fill="url(#hillGrad)"
      />
      {/* Peak glow */}
      <ellipse cx="300" cy="18" rx="30" ry="20" fill="rgba(255,107,0,0.15)" />
      <circle cx="300" cy="16" r="5" fill="rgba(255,107,0,0.3)" />
    </svg>
  );
}

/** Small airport runway marker */
export function AirportMarker({ size = 24, color = '#FF6B00' }) {
  return (
    <svg viewBox="0 0 30 30" width={size} height={size}>
      <circle cx="15" cy="15" r="13" fill="none" stroke={color} strokeWidth="1.5" opacity="0.6" />
      {/* Runway cross */}
      <rect x="5" y="13.5" width="20" height="3" rx="1" fill={color} opacity="0.8" />
      <rect x="13.5" y="5" width="3" height="20" rx="1" fill={color} opacity="0.8" />
    </svg>
  );
}
